import { useState } from 'react'
import api from '../api/axios'
import {
  BuildingOfficeIcon,
  UsersIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'

interface Employee {
  id: number
  employee_id: string
  first_name: string | null
  last_name: string | null
  name: string
  email: string | null
  role: string
  is_active: number
  hire_date: string | null
}

type ExportFormat = 'json' | 'csv'

export default function TenantDataExport() {
  const [companyCode, setCompanyCode] = useState('')
  const [employees, setEmployees]     = useState<Employee[]>([])
  const [loading, setLoading]         = useState(false)
  const [error, setError]             = useState<string | null>(null)
  const [search, setSearch]           = useState('')
  const [selectedEmp, setSelectedEmp] = useState<Employee | null>(null)
  const [exporting, setExporting]     = useState(false)
  const [exported, setExported]       = useState(false)
  const [format, setFormat]           = useState<ExportFormat>('json')

  const fetchEmployees = async () => {
    if (!companyCode.trim()) return
    setLoading(true)
    setError(null)
    setEmployees([])
    setSelectedEmp(null)
    setExported(false)
    try {
      const res = await api.get(`/super-admin/tenants/${companyCode.trim()}/employees`)
      setEmployees(res.data.employees || [])
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } }
      setError(e?.response?.data?.error || 'Failed to load employees')
    } finally {
      setLoading(false)
    }
  }

  const doExport = async () => {
    if (!selectedEmp) return
    setExporting(true)
    setExported(false)
    try {
      const url = `/super-admin/tenants/${companyCode}/employees/${selectedEmp.employee_id}/export?format=${format}`

      const res = await api.get(url, { responseType: 'blob' })

      const disposition = (res.headers['content-disposition'] as string) || ''
      const filenameMatch = disposition.match(/filename="([^"]+)"/)
      const filename = filenameMatch
        ? filenameMatch[1]
        : `${companyCode}-${selectedEmp.employee_id}-export.${format}`

      const href = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = href
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(href)
      setExported(true)
      setTimeout(() => setExported(false), 4000)
    } catch (err: unknown) {
      const e = err as { message?: string }
      setError(e.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const filteredEmployees = employees.filter(e => {
    const q = search.toLowerCase()
    return (
      e.name?.toLowerCase().includes(q) ||
      e.employee_id?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.first_name?.toLowerCase().includes(q) ||
      e.last_name?.toLowerCase().includes(q)
    )
  })

  const displayName = (e: Employee) =>
    e.first_name || e.last_name
      ? `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim()
      : e.name || e.employee_id

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm">
            <ArrowDownTrayIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 leading-tight">Tenant Data Export</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Download all data for an employee — timeclock shifts, notes, and break records. For GDPR requests, audits, or support.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">

      {/* Step 1: Company lookup */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">1</div>
          <div>
            <div className="font-semibold text-slate-800">Select Company</div>
            <div className="text-xs text-slate-500">Enter the company code to load its employees</div>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="relative flex-1">
            <BuildingOfficeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={companyCode}
              onChange={e => setCompanyCode(e.target.value.toLowerCase())}
              onKeyDown={e => e.key === 'Enter' && fetchEmployees()}
              placeholder="company-code"
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={fetchEmployees}
            disabled={loading || !companyCode.trim()}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors"
          >
            {loading ? 'Loading…' : 'Load Employees'}
          </button>
        </div>

        {error && (
          <div className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Step 2: Pick employee */}
      {employees.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">2</div>
            <div className="flex-1">
              <div className="font-semibold text-slate-800">Select Employee</div>
              <div className="text-xs text-slate-500">{employees.length} employee{employees.length !== 1 ? 's' : ''} in <span className="font-mono">{companyCode}</span></div>
            </div>
          </div>

          {/* Search filter */}
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter by name, ID, or email…"
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Employee list */}
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {filteredEmployees.map(emp => (
              <button
                key={emp.employee_id}
                onClick={() => { setSelectedEmp(emp); setExported(false) }}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl border transition-all text-left ${
                  selectedEmp?.employee_id === emp.employee_id
                    ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-400'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center flex-shrink-0">
                  <UsersIcon className="w-4 h-4 text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-800 truncate">{displayName(emp)}</div>
                  <div className="text-xs text-slate-500 truncate">
                    <span className="font-mono">{emp.employee_id}</span>
                    {emp.email && <span className="ml-2">· {emp.email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-slate-500 capitalize">{emp.role}</span>
                  <span className={`w-2 h-2 rounded-full ${emp.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                </div>
                {selectedEmp?.employee_id === emp.employee_id && (
                  <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                )}
              </button>
            ))}
            {filteredEmployees.length === 0 && (
              <p className="text-center text-sm text-slate-400 py-6">No employees match your filter.</p>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Export */}
      {selectedEmp && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">3</div>
            <div>
              <div className="font-semibold text-slate-800">Export Data</div>
              <div className="text-xs text-slate-500">All timeclock records for <strong>{displayName(selectedEmp)}</strong></div>
            </div>
          </div>

          {/* What's included */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Included in export</div>
            <div className="grid grid-cols-2 gap-2 text-sm text-slate-700">
              {[
                'Employee profile & contact info',
                'All clock-in / clock-out records',
                'GPS coordinates per punch',
                'Shift notes',
                'Break records',
                'Minutes worked per shift',
              ].map(item => (
                <div key={item} className="text-xs flex items-center gap-1.5">
                  <CheckIcon className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Format picker */}
          <div className="flex gap-3 mb-5">
            {(['json', 'csv'] as ExportFormat[]).map(f => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  format === f
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
              >
                {f.toUpperCase()}
                <div className="text-xs font-normal opacity-70 mt-0.5">
                  {f === 'json' ? 'Full structured data' : 'Spreadsheet-friendly'}
                </div>
              </button>
            ))}
          </div>

          {/* Download button */}
          <button
            onClick={doExport}
            disabled={exporting}
            className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all ${
              exported
                ? 'bg-green-600 text-white'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md shadow-blue-200'
            } disabled:opacity-60`}
          >
            {exporting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z" />
                </svg>
                Preparing export…
              </>
            ) : exported ? (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                Downloaded!
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="w-5 h-5" />
                Download {format.toUpperCase()} Export
              </>
            )}
          </button>

          <p className="text-center text-xs text-slate-400 mt-3 flex items-center justify-center gap-1.5">
            <ShieldCheckIcon className="w-3.5 h-3.5" />
            Export includes all historical data · This action is logged in the audit trail
          </p>
        </div>
      )}
      </div>
    </div>
  )
}
